import {
  Box,
  FormControl,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import React, { useContext, useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import * as Yup from "yup";

import http from "src/utils/http";
import Logo from "src/components/Logo";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller as Fc } from "react-hook-form";
import TreffasStyle from "src/utils/TreffasStyle";

import SideBg from "../SideBg";
import { useTranslation } from "react-i18next";
import { Users } from "src/context";
import { useLocation } from "react-router-dom";
import { useSnackbarContext } from "src/context/SnackbarContext";
import Page from "src/components/Page";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const VerifyCode = () => {
  const themes = useTheme();
  const [isProcess, setIsProcess] = useState(false);
  const searchParams = useQuery();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const client_type = searchParams.get("client_type");
  const { t } = useTranslation();
  const { mixpanel } = useContext(Users);
  const { showSnackbar } = useSnackbarContext();
  const [isResendCode, setIsResendCode] = useState(false);

  const Schema = Yup.object({
    code: Yup.string().required("Code is a required field."),
  }).required();

  const {
    handleSubmit,
    control,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      // organization: "",
      code: "",
      email: email,
      token: token,
      client_type: client_type,
    },
  });

  const onLoginVerifyCode = (values) => {
    setIsProcess(true);
    http
      .post("/api/oauth/login/client-portal/verify", {
        code: values.code,
        email: values.email,
        token: values.token,
        client_type: values.client_type,
      })
      .then(({ data }) => {
        if (data.message === "email-invalid") {
          showSnackbar("Email address is invalid.", "error");
        }

        if (data.message === "code-expired") {
          showSnackbar("The code has already expired.", "error");
        }

        if (data.message === "code-invalid") {
          setError("code", {
            required: true,
            message: "The code is invalid.",
          });
        }

        if (data.message === "success") {
          localStorage.setItem(`accessToken`, data.accessToken);
          localStorage.setItem(`__loginType`, "client");

          window.location.href = `/client/appointments`;
        }
      })
      .catch((err) => {
        console.log(err.message);
      })
      .finally(() => setIsProcess(false));
  };

  const handleResendCode = () => {
    if (isResendCode) {
      return;
    }

    setIsResendCode(true);
    const email = getValues("email");
    http
      .post("/api/oauth/login/client-portal", {
        email: email,
      })
      .then(({ data }) => {
        if (data.message === "success") {
          console.log("data:", data);
          // Update the token in the form
          setValue("token", data.token);
          // Update the URL without navigation
          window.history.replaceState(
            null,
            "",
            `/login/client-portal/verify?email=${email}&token=${data.token}`
          );
        }
      })
      .catch((err) => {
        console.log(err.message);
      })
      .finally(() => setIsResendCode(false));
  };

  React.useEffect(() => {
    mixpanel.track("Login Client Portal Verify Code Page", {
      location: window.location.href,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page title="Login Verification Code">
      <Box
        sx={(themes) => ({
          height: "80px",
          alignItems: "center",
          [themes.breakpoints.up("md")]: {
            alignItems: "center",
            boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.04)",
            pl: "99px",
            display: "none",
          },
          [themes.breakpoints.down("md")]: {
            justifyContent: "flex-start",
            pl: "24px !important",
            display: "flex",
          },
        })}
      >
        <Logo />
      </Box>

      <Box
        sx={{
          minHeight: { xs: "calc(100vh - 110px)", md: "calc(100vh - 0px)" },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            pt: {
              md: "120px",
            },
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
          }}
        >
          <Box>
            <Box>
              <Box mb={"12px"}>
                <Typography
                  sx={{
                    fontSize: "26px",
                    lineHeight: "26px",
                    textAlign: "center",
                    letterSpacing: "-0.26px",
                    fontFamily: "var(--font-grenettepro)",
                  }}
                >
                  Please verify your email address
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "15px",
                  textAlign: "center",
                }}
              >
                For security purposes, we’ve sent a 6-character <br /> code to{" "}
                <span style={{ color: "var(--color-brand-primary)" }}>{email} </span>.
                Please check <br /> your email and enter it below.
              </Typography>
            </Box>

            {/* login form */}
            <Box
              sx={{
                width: "440px",
                mt: "32px",
              }}
            >
              <form onSubmit={handleSubmit(onLoginVerifyCode)}>
                <Box>
                  <FormControl fullWidth variant="standard">
                    <Typography
                      sx={{
                        ...TreffasStyle.formLabel,
                      }}
                    >
                      6-character code
                    </Typography>

                    <Fc
                      name="code"
                      control={control}
                      rules={{
                        required: true,
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder={"Enter a code here..."}
                          type="text"
                          sx={TreffasStyle.formInputLarge("input")}
                          error={Boolean(errors.code)}
                          helperText={errors.code?.message}
                        />
                      )}
                    />
                  </FormControl>
                </Box>

                {errors.submit && (
                  <Box>
                    <FormHelperText
                      error
                      sx={{
                        textAlign: "center",
                      }}
                    >
                      {errors.submit.message}
                    </FormHelperText>
                  </Box>
                )}

                <Box
                  sx={{
                    mt: "16px",
                  }}
                >
                  <Link
                    href="/login/client-portal"
                    sx={{
                      textDecoration: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--color-brand-primary)",
                        fontFamily: "var(--font-grenettepro)",
                        textAlign: "right",
                        fontSize: "12px",
                      }}
                    >
                      Back to login
                    </Typography>
                  </Link>
                </Box>

                <Box sx={{ mt: "10px" }}>
                  <TreffasButton
                    variant="primary"
                    size="md"
                    fullWidth
                    type="submit"
                    loading={isProcess}
                    disabled={isProcess}
                  >
                    Continue
                  </TreffasButton>
                </Box>

                <Box
                  sx={{
                    mt: "12px",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  Didn’t receive the code?{" "}
                  <Link
                    sx={{
                      textDecoration: "none",
                    }}
                    onClick={handleResendCode}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--color-brand-primary)",
                        fontFamily: "var(--font-grenettepro)",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      {isResendCode ? " Resending..." : " Resend code"}
                    </Typography>
                  </Link>
                </Box>
              </form>
            </Box>
          </Box>
        </Box>

        <Box
          sx={() => ({
            overflow: "hidden",
            width: "1100px",
            [themes.breakpoints.down("lg")]: {
              display: "none",
            },
          })}
        >
          <SideBg />
        </Box>
      </Box>
    </Page>
  );
};

export default VerifyCode;
